import { useForm } from '@tanstack/react-form'
import { useEffect, useState } from 'react'
import { useUpdatePasswordMutation } from '../../auth/hooks'
import {
  ResetPasswordSchema,
  UpdatePasswordSchema,
} from '../../auth/schema/passwordSchema'
import LoadingButton from '@/components/custom/loading-button'
import PasswordInput from '@/components/custom/password-input'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { VisuallyHidden } from '@/components/ui/visually-hidden'
import { notifyRequestError } from '@/utils/notifyRequestError'
import { useIsMobile } from '@/hooks'

export function UpdatePassword() {
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)
  const {
    mutate: updatePasswordMutation,
    isSuccess,
    error,
    isPending,
  } = useUpdatePasswordMutation()
  const form = useForm({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    onSubmit: async ({ value }) => {
      await updatePasswordMutation(value)
    },
  })

  useEffect(() => {
    if (isSuccess) {
      setOpen(false)
    }
    if (error) {
      notifyRequestError(error, 'Failed to update password.')
    }
  }, [isSuccess, error])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm">
          Change Password
        </Button>
      </DialogTrigger>
      <DialogContent className={isMobile ? 'w-full' : 'w-fit'}>
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold mb-4 text-center">
            Update Password
          </DialogTitle>
          <VisuallyHidden>
            <DialogDescription>
              Update your account password by providing your current password
              and a new password.
            </DialogDescription>
          </VisuallyHidden>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
          className="space-y-3"
        >
          <form.Field
            name="currentPassword"
            validators={{
              onBlur: ({ value }) => {
                const result =
                  UpdatePasswordSchema.innerType().shape.currentPassword.safeParse(
                    value,
                  )
                return result.success
                  ? undefined
                  : result.error.errors[0].message
              },
            }}
            children={(field) => (
              <div className="gap-0">
                <PasswordInput
                  placeholder="••••••••"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  className="pr-10"
                  field={field}
                  label="Current Password"
                />
              </div>
            )}
          />
          <form.Field
            name="newPassword"
            validators={{
              onBlur: ({ value, fieldApi }) => {
                const result =
                  ResetPasswordSchema.innerType().shape.newPassword.safeParse(
                    value,
                  )
                if (!result.success) {
                  return result.error.errors[0].message
                }
                // Ensure newPassword is not the same as currentPassword
                const currentPassword =
                  fieldApi.form.getFieldValue('currentPassword')
                if (value === currentPassword) {
                  return 'New password must be different from current password.'
                }
                return undefined
              },
            }}
            children={(field) => (
              <div className="gap-0">
                <PasswordInput
                  placeholder="••••••••"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  className="pr-10"
                  showStrengthIndicator
                  field={field}
                  label="New Password"
                />
              </div>
            )}
          />
          <form.Field
            name="confirmPassword"
            validators={{
              onBlur: ({ value, fieldApi }) => {
                const result =
                  ResetPasswordSchema.innerType().shape.confirmPassword.safeParse(
                    value,
                  )
                if (!result.success) {
                  return result.error.errors[0].message
                }
                // Ensure confirmPassword matches newPassword
                const newPassword = fieldApi.form.getFieldValue('newPassword')
                if (value !== newPassword) {
                  return 'Passwords do not match.'
                }
                return undefined
              },
            }}
            children={(field) => (
              <div className="gap-0 mt-6 mb-8">
                <PasswordInput
                  placeholder="••••••••"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  className="pr-10"
                  field={field}
                  label="Confirm Password"
                />
              </div>
            )}
          />
          <form.Subscribe
            selector={(state) => [state.canSubmit]}
            children={([canSubmit]) => (
              <LoadingButton
                type="submit"
                className="w-full"
                isLoading={isPending}
                disabled={!canSubmit || isPending}
              >
                {isPending ? 'Updating...' : 'Update Password'}
              </LoadingButton>
            )}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
